/**
 * Floor plan designer room keys for Firestore `roomDesigns` map fields.
 * Firestore rejects field names that both start and end with "__".
 */

/** Single-room key when the event has no named design plans (legacy / v1). */
export const FLOOR_PLAN_DEFAULT_ROOM_KEY = '_default';

/** Pre-fix in-app key; normalised to {@link FLOOR_PLAN_DEFAULT_ROOM_KEY} on read and write. */
export const LEGACY_FLOOR_PLAN_DEFAULT_ROOM_KEY = '__default__';

export function normalizeFloorPlanRoomKey(roomId: string): string {
  return roomId === LEGACY_FLOOR_PLAN_DEFAULT_ROOM_KEY
    ? FLOOR_PLAN_DEFAULT_ROOM_KEY
    : roomId;
}

/** True when Firestore will reject the name as a document/map field key. */
export function isReservedFirestoreFieldName(fieldName: string): boolean {
  return fieldName.startsWith('__') && fieldName.endsWith('__');
}

export function assertFirestoreSafeRoomDesignKeys(roomDesigns: Record<string, unknown>): void {
  for (const key of Object.keys(roomDesigns)) {
    if (isReservedFirestoreFieldName(key)) {
      throw new Error(`Invalid Firestore field name: ${key}`);
    }
  }
}

export function normalizeRoomDesignsKeys<T>(roomDesigns: Record<string, T>): Record<string, T> {
  const normalized: Record<string, T> = {};
  for (const [key, value] of Object.entries(roomDesigns)) {
    normalized[normalizeFloorPlanRoomKey(key)] = value;
  }
  return normalized;
}

export interface FloorPlanRoomDesignSnapshot<E = unknown> {
  elements: E[];
  zoom: number;
  panX: number;
  panY: number;
  paperSize: string;
  orientation: string;
  frameStyle: string;
}

/**
 * Builds the `roomDesigns` map for floor plan auto-save.
 * Normalises cache keys so legacy `__default__` never reaches Firestore.
 */
export function buildRoomDesignsForFirestore<E>(
  cache: Record<string, FloorPlanRoomDesignSnapshot<E>>,
  currentRoomId: string | null,
  currentSnapshot: FloorPlanRoomDesignSnapshot<E>,
  stripElements: (elements: E[]) => E[]
): Record<string, FloorPlanRoomDesignSnapshot<E>> {
  const normalizedCurrentRoomId = currentRoomId
    ? normalizeFloorPlanRoomKey(currentRoomId)
    : null;

  const roomDesigns: Record<string, FloorPlanRoomDesignSnapshot<E>> = {};

  for (const [cacheKey, snapshot] of Object.entries(cache)) {
    const firestoreKey = normalizeFloorPlanRoomKey(cacheKey);
    if (normalizedCurrentRoomId && firestoreKey === normalizedCurrentRoomId) {
      continue;
    }
    roomDesigns[firestoreKey] = {
      ...snapshot,
      elements: stripElements(snapshot.elements),
    };
  }

  if (normalizedCurrentRoomId) {
    roomDesigns[normalizedCurrentRoomId] = {
      ...currentSnapshot,
      elements: stripElements(currentSnapshot.elements),
    };
  }

  assertFirestoreSafeRoomDesignKeys(roomDesigns);
  return roomDesigns;
}
